// ── Scope Assignment + Dashboard API hooks (D.2) ──────────────────────────────

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/store/auth.store';
import type {
  ScopeAssignment,
  EffectiveAssignment,
  CreateScopeAssignmentBody,
  UpdateScopeAssignmentBody,
  BulkAssignmentBody,
  MyAssignmentRow,
  WorkloadRow,
} from '@grc/shared';

// ── Query keys ────────────────────────────────────────────────────────────────

function scopeAssignmentsKey(tenantId: string | null, frameworkId: string, category?: string) {
  return ['scope-assignments', tenantId, frameworkId, category ?? null] as const;
}

function effectiveAssignmentKey(tenantId: string | null, controlId: string) {
  return ['effective-assignment', tenantId, controlId] as const;
}

function myAssignmentsKey(tenantId: string | null, filters?: { status?: string; frameworkId?: string }) {
  return ['my-assignments', tenantId, filters?.status, filters?.frameworkId] as const;
}

function workloadKey(tenantId: string | null) {
  return ['compliance-workload', tenantId] as const;
}

// ── Scope assignment queries ───────────────────────────────────────────────────

export function useScopeAssignments(frameworkId: string, category?: string) {
  const { tenantId } = useAuthStore();
  const params = new URLSearchParams({ framework_id: frameworkId });
  if (category !== undefined) params.set('category', category);
  return useQuery({
    queryKey: scopeAssignmentsKey(tenantId, frameworkId, category),
    queryFn: async () => {
      const { data } = await apiClient.get(`/compliance/scope-assignments?${params}`);
      return data.data as ScopeAssignment[];
    },
    enabled: !!tenantId && !!frameworkId,
    staleTime: 0,
  });
}

export function useEffectiveAssignment(controlId: string) {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: effectiveAssignmentKey(tenantId, controlId),
    queryFn: async () => {
      const { data } = await apiClient.get(`/compliance/assignments/effective?control_id=${controlId}`);
      return data.data as EffectiveAssignment;
    },
    enabled: !!tenantId && !!controlId,
    staleTime: 0,
  });
}

// ── Scope assignment mutations ─────────────────────────────────────────────────

export function useCreateScopeAssignment(frameworkId: string) {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateScopeAssignmentBody) => {
      const { data } = await apiClient.post('/compliance/scope-assignments', body);
      return data.data as ScopeAssignment;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['scope-assignments', tenantId, frameworkId] });
      qc.invalidateQueries({ queryKey: ['effective-assignment', tenantId] });
    },
  });
}

export function useUpdateScopeAssignment(frameworkId: string) {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateScopeAssignmentBody & { id: string }) => {
      const { data } = await apiClient.patch(`/compliance/scope-assignments/${id}`, body);
      return data.data as ScopeAssignment;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['scope-assignments', tenantId, frameworkId] });
      qc.invalidateQueries({ queryKey: ['effective-assignment', tenantId] });
    },
  });
}

export function useDeleteScopeAssignment(frameworkId: string) {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/compliance/scope-assignments/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['scope-assignments', tenantId, frameworkId] });
      qc.invalidateQueries({ queryKey: ['effective-assignment', tenantId] });
    },
  });
}

export function useClearScopeOverrides(frameworkId: string) {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post(`/compliance/scope-assignments/${id}/clear-overrides`, { confirm: true });
      return data.data as { cleared: number };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['scope-assignments', tenantId, frameworkId] });
      qc.invalidateQueries({ queryKey: ['effective-assignment', tenantId] });
      qc.invalidateQueries({ queryKey: ['assignments', tenantId] });
    },
  });
}

// ── Bulk assign mutation ───────────────────────────────────────────────────────

export function useBulkAssign() {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: BulkAssignmentBody) => {
      const { data } = await apiClient.post('/compliance/assignments/bulk', body);
      return data.data as { created: number; assignment_ids: string[] };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assignments', tenantId] });
      qc.invalidateQueries({ queryKey: ['effective-assignment', tenantId] });
    },
  });
}

// ── Dashboard queries ─────────────────────────────────────────────────────────

export function useMyAssignments(filters?: { status?: string; frameworkId?: string }) {
  const { tenantId } = useAuthStore();
  const params = new URLSearchParams();
  if (filters?.status) params.set('status', filters.status);
  if (filters?.frameworkId) params.set('framework_id', filters.frameworkId);
  const qs = params.toString();
  return useQuery({
    queryKey: myAssignmentsKey(tenantId, filters),
    queryFn: async () => {
      const { data } = await apiClient.get(`/compliance/assignments/my${qs ? '?' + qs : ''}`);
      return data.data as MyAssignmentRow[];
    },
    enabled: !!tenantId,
    staleTime: 30_000,
  });
}

export function useWorkload() {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: workloadKey(tenantId),
    queryFn: async () => {
      const { data } = await apiClient.get('/compliance/assignments/workload');
      return data.data as WorkloadRow[];
    },
    enabled: !!tenantId,
    staleTime: 30_000,
  });
}
